<template>
  <v-card flat>
    <v-row no-gutters>
      <v-col cols="12">
        <v-btn color="primary" icon @click="handleBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h6 mr-2" style="vertical-align: middle"
          >{{ $i18next.t('message.ai.dubbings.dubbing') }}
        </span>
        <helper-tooltip :click="handleKnowMore" />
      </v-col>
    </v-row>

    <v-row no-gutters class="px-2 dubbing-container">
      <v-col cols="12" class="pt-6 pb-0" v-if="hasEditVideoPerm">
        <v-row no-gutters>
          <v-col cols="12">
            <v-container class="rounded pa-0">
              <v-row no-gutters>
                <v-col cols="12">
                  <h4
                    class="font-weight-medium white--text text-h7 mb-4"
                    :class="{
                      'neutral--text text--darken-1': !$vuetify.theme.dark,
                    }"
                  >
                    {{ $i18next.t('message.ai.dubbings.availableDubs') }}
                  </h4>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </v-col>

      <v-card flat width="100%" v-if="allDubbings.length || loadings.fetching">
        <template v-if="loadings.fetching">
          <v-skeleton-loader
            type="image"
            height="48px"
            style="margin-top: 1px"
          ></v-skeleton-loader>
          <v-skeleton-loader
            type="image"
            height="48px"
            style="margin-top: 1px"
          ></v-skeleton-loader>
          <v-skeleton-loader
            type="image"
            height="34px"
            width="148px"
            class="mt-4"
          ></v-skeleton-loader>
        </template>

        <template v-else>
          <template v-if="allDubbings.length">
            <v-row no-gutters>
              <div
                v-for="(dubbing, i) in allDubbings"
                :key="`${i}__dub`"
                class="list-item__bordered f-width py-3 d-flex align-center justify-space-between"
              >
                <template v-if="dubbing.hasOwnProperty('metadata')">
                  <div class="d-flex align-end">
                    <country-flag
                      :country="
                        getCountryCodeBySrcLang(
                          dubbing.metadata.to_lang || dubbing.metadata.from_lang
                        )
                      "
                      class="list-item__flag"
                    />
                    <span
                      class="font-weight-medium text-subtitle-2 text-subtitle-2__standardized"
                      :class="{
                        'neutral--text text--lighten-1': !$vuetify.theme.dark,
                      }"
                    >
                      {{
                        getLangLabelBySrclang(
                          dubbing.metadata.to_lang || dubbing.metadata.from_lang
                        )
                      }}
                    </span>
                  </div>
                  <div
                    class="d-flex align-center"
                    v-if="
                      dubbing.status === 'QUEUE' || dubbing.status === 'RUNNING'
                    "
                  >
                    <div
                      class="mr-1 d-flex align-center"
                      style="position: relative"
                    >
                      <v-progress-circular
                        :size="16"
                        :width="3"
                        color="brand"
                        style="top: 0; position: absolute"
                      ></v-progress-circular>
                      <v-progress-circular
                        :size="16"
                        :width="3"
                        color="brand"
                        indeterminate
                      ></v-progress-circular>
                    </div>
                    <span
                      :class="{
                        'neutral--text text--lighten-1': !$vuetify.theme.dark,
                      }"
                      >{{
                        $i18next.t(
                          'message.ai.dubbings.modal.auto.final_screen.title'
                        )
                      }}</span
                    >
                  </div>
                </template>
                <template v-else>
                  <div class="d-flex align-end">
                    <country-flag
                      :country="getCountryCodeBySrcLang(dubbing.lang)"
                      class="list-item__flag"
                    />
                    <span
                      class="font-weight-medium text-subtitle-2 text-subtitle-2__standardized"
                      :class="{ 'brand--text': !$vuetify.theme.dark }"
                    >
                      {{ getLangLabelBySrclang(dubbing.lang) }}
                    </span>
                    <span
                      class="ml-2 text-caption blueMarine--text align-self-center"
                      v-if="!dubbing.type || dubbing.type !== 'UPLOADED'"
                    >
                      powered by <b>IIElevenLabs</b>
                    </span>
                  </div>
                </template>

                <div
                  class="d-flex align-center"
                  v-if="
                    dubbing.status === 'ERROR' ||
                    dubbing.status === 'FAILED' ||
                    dubbing.status === 'CONVERTED' ||
                    dubbing.status === 'SKIPPED' ||
                    (!dubbing.status && dubbing.type !== 'UPLOADED')
                  "
                >
                  <span
                    class="mr-4 red--text text-subtitle-2 font-weight-regular text-subtitle-2__standardized"
                    v-if="
                      dubbing.status === 'ERROR' || dubbing.status === 'FAILED'
                    "
                    >{{ $i18next.t('message.ai.dubbings.error') }}</span
                  >
                  <template v-else-if="dubbing.status === 'SKIPPED'">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="{ ...on, ...$listeners }"
                          class="mr-4 warning--text"
                        >
                          {{ returnDubbingSkippedMessage }}
                        </span>
                      </template>
                      <span>
                        {{ $i18next.t('message.ai.skippedWarning') }}:
                        {{ getBlockingItem(dubbing) }}</span
                      >
                    </v-tooltip>
                  </template>
                  <v-icon
                    color="red"
                    size="24px"
                    @click="handleConfirmDeleteDubbing(dubbing)"
                  >
                    mdi-delete
                  </v-icon>
                </div>
                <div 
                  class="d-flex align-center"
                  v-else-if="['UPLOADED', 'DRAFT', 'CONVERTING'].includes(dubbing.status) || (!dubbing.hasOwnProperty('status') && dubbing.type === 'UPLOADED')"
                >
                    <span
                      class="ml-2 secondaryText--text caption noIbar"
                    >
                      <v-icon>mdi-progress-upload</v-icon>
                      {{ $t('message.converting') }}...
                  </span>
                </div>
              </div>
            </v-row>
            <BaseBtn class="mt-4" size="small" @click="handleOpenAddDubbing">{{
              $i18next.t('message.ai.dubbings.addDubbing')
            }}</BaseBtn>
            <v-card v-if="errors.audioRemove" class="mt-1" flat width="100%">
              <span class="red--text caption align-text">
                <v-icon color="red" size="18" class="pb-1 pr-1">
                  mdi-alert-circle
                </v-icon>
                {{ errors.unknownErrorsMsg
                }}<template v-if="errors.unknownErrorsCode"
                  >. Code:

                  {{ errors.unknownErrorsCode }}</template
                >
              </span>
            </v-card>
          </template>

          <template
            v-if="allDubbings.length && unusedDubbingLangCodesAi.length"
          >
            <SuggestedAiWorkflows
              :title="$i18next.t('message.ai.dubbings.suggestedDubs')"
              :itemsList="unusedDubbingLangCodesAi"
              :btnText="`${$i18next.t('message.ai.dubbings.generateDubbing')}`"
              :btnDefaultAction="handleOpenReadyModal"
            />
          </template>
        </template>
      </v-card>
      <template v-else>
        <v-card outlined width="100%" class="pa-4 v-card__ai-empty-state-info">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex flex-column">
              <span
                class="subtitle-1 font-weight-medium"
                :class="{ 'neutral--text': !$vuetify.theme.dark }"
                >{{
                  $i18next.t('message.ai.dubbings.emptyState.headCard.title')
                }}</span
              >
              <span
                class="subtitle-2"
                :class="{
                  'neutral--text text--lighten-1': !$vuetify.theme.dark,
                }"
                >{{
                  $i18next.t('message.ai.dubbings.emptyState.headCard.subtitle')
                }}</span
              >
            </div>
            <div class="d-flex flex-column align-center">
              <img src="@/assets/robot.svg" />
              <v-chip small class="ai-chip panda-ia-chip"
                ><span
                  class="font-weight-medium neutral--text text--lighten-1"
                  >{{ $i18next.t('message.ai.ai') }}</span
                ></v-chip
              >
            </div>
          </div>
        </v-card>

        <v-card
          outlined
          width="100%"
          class="pa-12 text-center v-card__ai-empty-state mt-4"
        >
          <simple-svg
            :src="require('@/assets/dubbing.svg')"
            fill-class-name="fill-color"
            fill="var(--v-neutral-lighten2)"
            width="48px"
          />
          <div>
            <span
              class="text-body-1 font-weight-medium"
              :class="{ 'neutral--text text--lighten-1': !$vuetify.theme.dark }"
              >{{
                $i18next.t('message.ai.dubbings.emptyState.createCard.title')
              }}</span
            ><br />
            <span
              class="text-body-2"
              :class="{ 'neutral--text text--lighten-1': !$vuetify.theme.dark }"
              >{{
                $i18next.t('message.ai.dubbings.emptyState.createCard.subtitle')
              }}</span
            >
            <v-row no-gutters justify="center" class="mt-4">
              <BaseBtn @click="handleOpenAddDubbing">
                {{
                  $i18next.t(
                    'message.ai.dubbings.emptyState.createCard.actionBtn'
                  )
                }}
              </BaseBtn>
            </v-row>
          </div>
        </v-card>
      </template>
    </v-row>
    <AiDeleteModal
      :title="$i18next.t('message.ai.dubbings.deleteDubTitle')"
      modalKey="Dubbing"
      :description="$i18next.t('message.ai.dubbings.deleteDubMessage')"
      @confirm="
        $root.$emit('closeAiDeleteModalDubbing'),
          $root.$emit('openAiDeleteConfirmModalDubbing')
      "
    />
    <AiConfirmDeleteModal
      :title="$i18next.t('message.ai.dubbings.deleteDubTitle')"
      :onConfirmLogin="handleDeleteDubbing"
      modalKey="Dubbing"
    />
  </v-card>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex'
import AiDeleteModal from '@/components/AddSubtitleModal/AiDeleteModal.vue'
import AiConfirmDeleteModal from '@/components/AddSubtitleModal/AiConfirmDeleteModal.vue'
import SuggestedAiWorkflows from '@/components/AddSubtitleModal/SuggestedAiWorkflows.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import i18n from '@/translations/lang'
import videosApi from '@/services/videosApi'
import aiworkflowApi from '@/services/aiworkflowApi'
import CountryFlag from 'vue-country-flag'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
import linkHelper from '@/utils/linkHelper.mixin.js'
import HelperTooltip from '@/components/HelperTooltip.vue'

export default {
  name: 'VideoPlayerDubbings',
  mixins: [linkHelper, aiWorkflowMixin],
  components: {
    HelperTooltip,
    BaseBtn,
    CountryFlag,
    SuggestedAiWorkflows,
    AiDeleteModal,
    AiConfirmDeleteModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
  },
  data: () => ({
    allAiWorkflowData: [],
    loadAiTimeout: null,
    videoInfoInterval: null,
    dubbingToBeDeleted: {},
    langsBeingRemoved: [],
    errors: {
      audioRemove: false,
      unknownErrorsCode: '',
      unknownErrorsMsg: '',
    },
    loadings: {
      audioDelete: false,
      fetching: true,
    },
    loopCheck: false,
    tabHidden: false,
  }),
  beforeDestroy() {
    this.allAiWorkflowData = []
    this.$root.$off('addAiWorkflowDubbing')
    this.UPDATE_GENERATING_DUBBING_DATA([])
    clearTimeout(this.loadAiTimeout)
    clearInterval(this.videoInfoInterval)
    this.loopCheck = false
  },
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id

    if (!this.subtitles) {
      this.$store.state.video.video.config.subtitles = []
    }
    if (!this.activeAudios) {
      this.$store.state.video.video.audios = []
    }
    const _this = this
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState == 'visible') {
        this.tabHidden = false
        if (this.loopCheck) {
          this.loadAiTimeout = setTimeout(
            () => _this.handleFetchDubbings(true),
            10 * 1000
          )
        }
        if (this.hasAConvertingAudio(this.allDubbings)) {
          this.setupVideoInfoInterval()
        }
      } else {
        this.tabHidden = true
        clearTimeout(this.loadAiTimeout)
        clearInterval(this.videoInfoInterval)
      }
    })

    this.handleFetchDubbings()
    this.$root.$on('addAiWorkflowDubbing', this.handleFetchDubbings)
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      profile: (state) => state.video.profile,
      profile_id: (state) => state.video.profile_id,
      subtitles: (state) => state.video.video.config.subtitles,
    }),
    isLoading() {
      return Boolean(
        Object.values(this.loadings).filter((value) => value).length
      )
    },
    allDubbings: {
      get() {
        const activeAudios = this.activeAudios ?? []
        let audiosFromVideo = this.video.audios ?? []
        if (audiosFromVideo.length > 0) {
          audiosFromVideo = audiosFromVideo.filter(audio => {
            return !activeAudios.some(activeAudio => activeAudio.lang === audio.lang)
          })
        }
        const audios = activeAudios.concat(audiosFromVideo)
        const list = [...audios, ...this.dubbingGeneratingData]
        return list
      },
      cache: false,
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    returnDubbingSkippedMessage() {
      const noWordsFound = this.allAiWorkflowData.some(
        (item) => item.metadata?.no_words_found
      )
      return noWordsFound ? this.$i18next.t('message.ai.noWordsFound') : this.$i18next.t('message.ai.skipped')
    },
    videoAudios() {
      return this.video.audios ?? []
    }
  },
  methods: {
    ...mapActions('video', ['fetchSubtitles']),
    handleKnowMore() {
      const link = this.getAiDubbingLink()
      window.open(link, '_blank')
    },
    handleConfirmDeleteDubbing(dubbing) {
      this.dubbingToBeDeleted = dubbing
      this.$root.$emit('openAiDeleteModalDubbing')
    },
    getBlockingItem(dubbing) {
      const blockItem = this.allAiWorkflowData.filter((item) => {
        if (item.metadata?.dependent_works) {
          return item.metadata.dependent_works.includes(dubbing.id)
        } else if (item.metadata?.dependent_work) {
          return dubbing.id === item.metadata.dependent_work
        }
        return false
      })
      if (!blockItem.length) return 'null'
      return this.getLangCodeBySrcLang(
        blockItem[0].metadata.to_lang ?? blockItem[0].metadata.from_lang
      ).label
    },
    async deleteAiWorkflow(dubbing) {
      this.loadings.audioDelete = true
      const langsBeingRemovedlastIndex = this.langsBeingRemoved.length - 1
      this.langsBeingRemoved.push(
        dubbing.metadata.to_lang ?? dubbing.metadata.from_lang
      )
      await aiworkflowApi.deleteAiWorkflow(dubbing.id)

      const newData = this.dubbingGeneratingData.filter(
        (item) =>
          (item.metadata?.to_lang ?? item.metadata?.from_lang) !=
          (dubbing.metadata?.to_lang ?? dubbing.metadata?.from_lang)
      )
      this.UPDATE_GENERATING_DUBBING_DATA(newData)
      this.langsBeingRemoved.splice(langsBeingRemovedlastIndex, 1)

      this.loadings.audioDelete = false
    },
    async handleDeleteDubbing() {
      try {
        if (this.dubbingToBeDeleted?.metadata) {
          await this.deleteAiWorkflow(this.dubbingToBeDeleted)
        } else {
          await this.handleRemoveDubbing(this.dubbingToBeDeleted)
        }
      } catch (e) {
        console.error(e)
        if (e?.response?.data) {
          this.errors.unknownErrorsCode = e.response.data.errCode
          this.errors.unknownErrorsMsg = e.response.data.errMsg
        } else if (e?.message) {
          this.errors.generate = e.message
        } else {
          this.errors.unknownErrorsMsg = i18n.t(
            'message.errors.unexpectedError'
          )
        }
        this.errors.audioRemove = true
      }
      this.$root.$emit('closeAiDeleteConfirmModalSubtitle')
    },
    async handleFetchDubbings(isLoopingCheck) {
      if (this.tabHidden && this.loopCheck) return
      if (isLoopingCheck) {
        this.loopCheck = isLoopingCheck
      }

      const { data } = await aiworkflowApi.getAiWorkflow(this.videoId)
      this.allAiWorkflowData = data
      const unfinishedDubbings = data.filter(
        (item) => item.status !== 'FINISHED' && item.type === 'DUBBING'
      )

      const onlyInA = this.dubbingGeneratingData.filter(
        (leftValue) =>
          !unfinishedDubbings.some(
            (rightValue) => leftValue.id === rightValue.id
          ) && data.some((item) => item.id === leftValue.id)
      )

      const _this = this
      if (onlyInA.length) {
        onlyInA.map((item) => {
          if (Array.isArray(_this.activeAudios)) {
            this.UPDATE_AUDIO_LIST([
              ...this.activeAudios,
              {
                id: item.metadata.audio_id,
                lang: item.metadata.from_lang,
                label: this.getLangLabelBySrclang(item.metadata.from_lang),
              },
            ])
          } else {
            this.UPDATE_AUDIO_LIST([
              {
                id: item.metadata.audio_id,
                lang: item.metadata.from_lang,
                label: this.getLangLabelBySrclang(item.metadata.from_lang),
              },
            ])
          }
          window.db.videos.update(this.videoId, {
            audios: this.activeAudios.map((item) => item.lang),
          })
          this.$root.$emit('forceRecomputeEmbedPage')
        })
        this.fetchSubtitles({
          libraryId: this.video.pullzone_name,
          videoId: this.video.video_external_id,
        })
      }

      this.UPDATE_GENERATING_DUBBING_DATA(unfinishedDubbings)
      const dubbingsBeingGenerated = this.dubbingGeneratingData.filter(
        (item) => item.status === 'QUEUE' || item.status === 'RUNNING'
      )

      if (!isLoopingCheck) {
        this.loadings.fetching = false
      }

      if (dubbingsBeingGenerated.length > 0) {
        _this.loadAiTimeout = setTimeout(
          () => _this.handleFetchDubbings(true),
          10 * 1000
        )
      } else {
        this.loopCheck = false
      }
    },
    handleOpenReadyModal() {
      this.$root.$emit('openDubbingModal', {
        videoId: this.videoId,
        initialState: true,
      })
    },
    ...mapMutations('video', [
      'UPDATE_VIDEO',
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_AUDIO_LIST',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    handleOpenAddDubbing() {
      this.$root.$emit('openDubbingModal', { videoId: this.videoId })
    },
    handleSnackbar(item) {
      if (!item) {
        item = this.$i18next.t('message.navDrawer.successMessage')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
    async handleRemoveDubbing(dubbingItem) {
      this.clearErrors()
      this.clearErrorMsg()
      const langsBeingRemovedlastIndex = this.langsBeingRemoved.length - 1
      this.langsBeingRemoved.push(dubbingItem.lang)
      this.loadings.audioDelete = true
      try {
        await videosApi.deleteAudioFromVideo(dubbingItem.id)
        this.loadings.audioDelete = false
        const audiosList = this.activeAudios.filter(
          (audio) => audio.lang != dubbingItem.lang
        )
        this.UPDATE_AUDIO_LIST(audiosList)
        this.$forceUpdate()
        this.$root.$emit('forceRecomputeEmbedPage')
        this.langsBeingRemoved.splice(langsBeingRemovedlastIndex, 1)
        this.loadings.audioDelete = false
      } catch (e) {
        if (e?.response?.data) {
          this.errors.unknownErrorsCode = e.response.data.errCode
          this.errors.unknownErrorsMsg = e.response.data.errMsg
        } else {
          this.errors.unknownErrorsMsg = i18n.t(
            'message.errors.unexpectedError'
          )
        }
        this.errors.audioRemove = true
        this.loadings.audioDelete = false

        this.langsBeingRemoved.splice(langsBeingRemovedlastIndex, 1)
        this.loadings.audioDelete = false
        throw e
      }
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    clearErrorMsg() {
      this.errors.unknownErrorsMsg = ''
    },
    onScroll() {
      this.scrollInvoked++
    },
    changeState() {
      this.video.config.subtitles = this.subtitles
      this.UPDATE_VIDEO(this.video)
    },
    setupVideoInfoInterval() {
      clearInterval(this.videoInfoInterval)
      this.videoInfoInterval = setInterval(async () => {
        const { data } = await videosApi.getAVideo(this.videoId)
        if (!this.hasAConvertingAudio(data.audios) || !this.$router.fullPath.includes(this.videoId)) {
          console.log('cleared from function')
          clearInterval(this.videoInfoInterval)
        }
      }, 10000)
    },
    hasAConvertingAudio(dubbingArray) {
      const convertingStatuses = ['UPLOADED', 'DRAFT', 'CONVERTING']
      return dubbingArray.some(
        (item) => convertingStatuses.includes(item.status) ||
        (!item.hasOwnProperty('status') && item.type === 'UPLOADED')
      )
    }
  },
  watch: {
    allDubbings: {
      handler(allDubbings) {
        if (this.hasAConvertingAudio(allDubbings)) 
          this.setupVideoInfoInterval()
      },
      deep: true,
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';
</style>

<style scoped lang="scss">
::v-deep .dubbing-container * {
  font-family: 'Rubik', sans-serif !important;
}

.v-card__ai-empty-state {
  border-radius: 4px;
  border: 1px solid var(--v-neutral-lighten3);

  &-info {
    margin-top: 1px;
    border-radius: 4px;
    border: 1px solid var(--v-brand-base);

    img {
      width: 48px;
      margin-right: 10px;
      margin-bottom: 2px;
    }

    .ai-chip {
      position: absolute;
      bottom: 6px;
      right: 10px;
      bottom: 4px;
    }
  }
}

.panda-ia-chip {
  background: var(--v-brand-lighten2) !important;
}
</style>
